// src/components/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Edit, Trash2, X, Save, Calendar, MapPin, Plus, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import ImageUpload from './ImageUpload';
import API_BASE_URL from '../config/api';

const AdminDashboard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingImages, setViewingImages] = useState(null);
  const [creatingEvent, setCreatingEvent] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    date: '',
    location: '',
    description: '',
    type: '',
    status: ''
  });

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    date: '',
    location: '',
    description: '',
    type: 'Corporate',
    status: 'Upcoming'
  });

 
const API_URL = `${API_BASE_URL}/api`;

  // Fetch all events
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data);
      console.log('✅ Fetched', res.data.length, 'events');
    } catch (err) {
      console.error('❌ Error fetching events:', err);
      alert('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const handleEdit = (event) => {
    setEditingEvent(event);
    setEditForm({
      name: event.name,
      date: event.date.split('T')[0],
      location: event.location,
      description: event.description || '',
      type: event.type,
      status: event.status
    });
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  // Handle create form change
  const handleCreateChange = (e) => {
    setCreateForm({
      ...createForm,
      [e.target.name]: e.target.value
    });
  };

  // Create new event
  const handleCreate = async () => {
    if (!createForm.name || !createForm.date || !createForm.location) {
      alert('Please fill all required fields (Name, Date, Location)');
      return;
    }

    try {
      console.log('📤 Creating new event...');
      
      const res = await axios.post(`${API_URL}/events`, createForm);

      setEvents([res.data, ...events]);
      setCreatingEvent(false);
      setCreateForm({
        name: '',
        date: '',
        location: '',
        description: '',
        type: 'Corporate',
        status: 'Upcoming'
      });
      alert('✅ Event created successfully!');
      console.log('✅ Event created:', res.data);
    } catch (err) {
      console.error('❌ Error creating event:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        alert('❌ You are not authorized to create events. Please login as admin.');
      } else {
        alert('❌ Failed to create event');
      }
    }
  };

  // Update event
  const handleUpdate = async () => {
    try {
      console.log('📤 Updating event:', editingEvent._id);
      
      const res = await axios.put(`${API_URL}/events/${editingEvent._id}`, editForm);

      setEvents(events.map(e => e._id === res.data._id ? res.data : e));
      setEditingEvent(null);
      alert('✅ Event updated successfully!');
      console.log('✅ Event updated:', res.data);
    } catch (err) {
      console.error('❌ Error updating event:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        alert('❌ You are not authorized to update events. Please login as admin.');
      } else {
        alert('❌ Failed to update event');
      }
    }
  };

  // Image management
  const handleViewImages = (event) => {
    setViewingImages(event);
  };

  const handleImagesUpdated = (updatedEvent) => {
    setEvents(events.map(e => e._id === updatedEvent._id ? updatedEvent : e));
    setViewingImages(updatedEvent);
  };

  // Delete event
  const handleDelete = async (eventId) => {
    try {
      console.log('🗑️ Deleting event:', eventId);
      
      await axios.delete(`${API_URL}/events/${eventId}`);

      setEvents(events.filter(e => e._id !== eventId));
      setDeleteConfirm(null);
      alert('✅ Event deleted successfully!');
      console.log('✅ Event deleted');
    } catch (err) {
      console.error('❌ Error deleting event:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        alert('❌ You are not authorized to delete events. Please login as admin.');
      } else {
        alert('❌ Failed to delete event');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header with Create Button */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Diplomata', cursive" }}>
                Event Management
              </h1>
              <p className="text-gray-600" style={{ fontFamily: "'Hepta Slab', serif" }}>
                Manage all your events
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-3xl font-bold text-orange-500">{events.length}</p>
                <p className="text-sm text-gray-600">Total Events</p>
              </div>
              <button
                onClick={() => setCreatingEvent(true)}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-green-700 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                Create Event
              </button>
            </div>
          </div>
        </div>

        {/* Events Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <p className="text-xl text-gray-600 mb-4">No events found</p>
            <p className="text-gray-500 mb-6">Create your first event to get started!</p>
            <button
              onClick={() => setCreatingEvent(true)}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-green-700 transition-all inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Your First Event
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">Event Name</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Location</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {events.map((event, index) => (
                    <tr key={event._id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">{event.name}</p>
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-1">{event.description}</p>
                        )}
                        {event.images && event.images.length > 0 && (
                          <p className="text-xs text-green-600 mt-1">📸 {event.images.length} image(s)</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {formatDate(event.date)}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {event.location}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                          {event.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          event.status === 'Upcoming' ? 'bg-green-100 text-green-800' :
                          event.status === 'Ongoing' ? 'bg-yellow-100 text-yellow-800' :
                          event.status === 'Completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(event)}
                            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewImages(event)}
                            className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                            title="Manage Images"
                          >
                            <ImageIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(event)}
                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {events.map((event) => (
                <div key={event._id} className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">{event.name}</h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mb-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(event.date)}
                      </p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </p>
                      {event.images && event.images.length > 0 && (
                        <p className="text-xs text-green-600 mt-1">📸 {event.images.length} image(s)</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(event)}
                        className="p-2 bg-blue-500 text-white rounded-lg"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewImages(event)}
                        className="p-2 bg-green-500 text-white rounded-lg"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(event)}
                        className="p-2 bg-red-500 text-white rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                      {event.type}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      event.status === 'Upcoming' ? 'bg-green-100 text-green-800' :
                      event.status === 'Ongoing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {event.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Event Modal */}
        {creatingEvent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 flex justify-between items-center sticky top-0">
                <h2 className="text-2xl font-black" style={{ fontFamily: "'Diplomata', cursive" }}>
                  Create New Event
                </h2>
                <button
                  onClick={() => setCreatingEvent(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Event Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={createForm.name}
                    onChange={handleCreateChange}
                    required
                    placeholder="e.g., Corporate Annual Meeting 2025"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={createForm.date}
                    onChange={handleCreateChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={createForm.location}
                    onChange={handleCreateChange}
                    required
                    placeholder="e.g., Bangalore, India"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={createForm.description}
                    onChange={handleCreateChange}
                    rows="4"
                    placeholder="Brief description of the event..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Type</label>
                    <select
                      name="type"
                      value={createForm.type}
                      onChange={handleCreateChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none"
                    >
                      <option value="Corporate">Corporate</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Product Launch">Product Launch</option>
                      <option value="Exhibition">Exhibition</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                    <select
                      name="status"
                      value={createForm.status}
                      onChange={handleCreateChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none"
                    >
                      <option value="Upcoming">Upcoming</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setCreatingEvent(false)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Create Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingEvent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex justify-between items-center">
                <h2 className="text-2xl font-black" style={{ fontFamily: "'Diplomata', cursive" }}>
                  Edit Event
                </h2>
                <button
                  onClick={() => setEditingEvent(null)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Event Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={editForm.date}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={editForm.location}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={editForm.description}
                    onChange={handleEditChange}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Type</label>
                    <select
                      name="type"
                      value={editForm.type}
                      onChange={handleEditChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
                    >
                      <option value="Corporate">Corporate</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Product Launch">Product Launch</option>
                      <option value="Exhibition">Exhibition</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                    <select
                      name="status"
                      value={editForm.status}
                      onChange={handleEditChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none"
                    >
                      <option value="Upcoming">Upcoming</option>
                      <option value="Ongoing">Ongoing</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setEditingEvent(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-bold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Update Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Management Modal */}
        {viewingImages && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 flex justify-between items-center sticky top-0">
                <div>
                  <h2 className="text-2xl font-black" style={{ fontFamily: "'Diplomata', cursive" }}>
                    Manage Images
                  </h2>
                  <p className="text-sm opacity-90 mt-1">{viewingImages.name}</p>
                </div>
                <button
                  onClick={() => setViewingImages(null)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                <ImageUpload
                  eventId={viewingImages._id}
                  currentImages={viewingImages.images || []}
                  onImagesUpdated={handleImagesUpdated}
                />
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-t-2xl">
                <h2 className="text-2xl font-black" style={{ fontFamily: "'Diplomata', cursive" }}>
                  Delete Event?
                </h2>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-2 font-bold">
                  Are you sure you want to delete:
                </p>
                <p className="text-xl font-black text-gray-900 mb-4">
                  "{deleteConfirm.name}"
                </p>
                <p className="text-red-600 font-bold mb-6">
                  ⚠️ This action cannot be undone!
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm._id)}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-bold hover:from-red-600 hover:to-red-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
// ```

// ---

// ## **✅ Now Test the Complete Workflow!**

// ### **Step 1: Login**
// - Go to: `http://localhost:3001/login`
// - Login with: `admin@omnifair.com` / `admin123`

// ### **Step 2: Create Event**
// 1. Click green **"Create Event"** button
// 2. Fill in:
//    - Name: `Tech Conference 2025`
//    - Date: `2025-02-15`
//    - Location: `Bangalore, India`
//    - Description: `Annual technology conference`
//    - Type: `Corporate`
//    - Status: `Upcoming`
// 3. Click **"Create Event"**
// 4. ✅ Event created!

// ### **Step 3: Upload Images**
// 1. Click green **image icon** (📸)
// 2. Select 2-3 images
// 3. Click **"Upload X Image(s)"**
// 4. ✅ Images uploaded to Cloudinary!

// ### **Step 4: View on Website**
// 1. Go to: `http://localhost:3001`
// 2. Scroll to **Gallery section**
// 3. ✅ See your event images!

// ---

// ## **🎯 Complete Workflow Now Works:**
// ```
// Client Contact → Admin Creates Event → Upload Photos → Visible on Website